import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { AxiosError } from 'axios';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import { COMMAND_HISTORY_QUERY_ROOT } from '@/composables/api/useSchedulerSyncApi';

export type PlayerLogRange = 'all' | '1h' | '24h' | '7d' | '30d';

export interface PlayerLogFilters {
    range: PlayerLogRange;
    message: string;
    /** `not_contains` inverts the message search — subtract noise, don't select signal. */
    messageMode: 'contains' | 'not_contains';
    track: string;
    /** Log-line prefixes (PLAYLIST, SYNC, POSTBOOT, …). Empty = every type. */
    types: string[];
    /** `not_in` hides the selected types instead of showing only them. */
    typesMode: 'in' | 'not_in';
}

/**
 * How many log lines of each type the server saw while scanning. The count is
 * not shown in the picker — it orders the options, most common first.
 */
export interface PlayerLogTypeCount {
    type: string;
    count: number;
}

export interface PlayerLogsMeta {
    /** Items DynamoDB examined — filters scan until enough matches accumulate. */
    scanned_count: number;
    /** True when the scan cap was hit before the window was exhausted. */
    scan_limited: boolean;
    /** True when the track filter resolved its 50-code cap and dropped the rest. */
    track_codes_limited: boolean;
    /**
     * Every log type in the scanned window with its count, most common first.
     * The picker's choices come from here rather than a hardcoded list, so a
     * type a player only just started sending still shows up. It reflects the
     * scanned window: a type that only occurs outside the chosen time range is
     * not offered.
     */
    types: PlayerLogTypeCount[];
}

export interface PlayerLogsResponse {
    data: PlayerLogEntry[];
    meta: PlayerLogsMeta;
}

const RANGE_SECONDS: Record<Exclude<PlayerLogRange, 'all'>, number> = {
    '1h': 3_600,
    '24h': 86_400,
    '7d': 604_800,
    '30d': 2_592_000,
};

/**
 * Server-side filter params for the logs endpoint. The time range is resolved
 * to a unix `from` at request time (not in the query key) so the sliding
 * window follows the 30s refetch without churning the cache key.
 */
export function buildLogQueryParams(
    filters: PlayerLogFilters,
    nowMs: number
): Record<string, string | string[]> {
    const params: Record<string, string | string[]> = {};
    if (filters.range !== 'all') {
        params.from = String(Math.floor(nowMs / 1000) - RANGE_SECONDS[filters.range]);
    }
    const message = filters.message.trim();
    if (message !== '') {
        params.message = message;
        if (filters.messageMode === 'not_contains') {
            params.message_mode = 'not_contains';
        }
    }
    const track = filters.track.trim();
    if (track !== '') {
        params.track = track;
    }
    if (filters.types.length > 0) {
        // Axios serializes an array as types[]=A&types[]=B, which is the shape
        // Laravel validates. Sorted so re-picking the same set in a different
        // order doesn't re-key the query and re-run the scan.
        params.types = [...filters.types].sort();
        if (filters.typesMode === 'not_in') {
            params.types_mode = 'not_in';
        }
    }
    return params;
}

/**
 * Brand a location zone player resolves to, computed server-side (tabasco
 * `joel/sau-259-player-brand`) and appended to LZP search rows and show
 * responses as `brand`. NOT in the generated `src/ts` interfaces yet — they
 * regenerate only after the backend merges — so it is typed locally here.
 * Readers must treat an absent/unrecognized value as unknown (never assume
 * GrayV): rows served by an older backend simply lack the field.
 */
export type LocationZonePlayerBrand = 'grayv' | 'tpg';

/** Local extension for rows/responses that may carry the computed `brand`. */
export interface LocationZonePlayerBrandFields {
    brand?: LocationZonePlayerBrand | string | null;
}

export interface PendingCommandsResponse {
    /** Commands already in the legacy Redis list, oldest-first (delivery order). */
    waiting: string[];
    /** Queue rows not yet dispatched to Redis by the phpapi scheduler. */
    queued: Array<{
        id: number;
        command: string;
        scheduled_time: string | null;
        created_at: string | null;
    }>;
}

/**
 * What a log entry's track carries — the fields the shared compact track card
 * shows, so a track in the logs looks like the same track everywhere else.
 */
export interface PlayerLogTrack {
    id: number;
    code: string | null;
    name: string | null;
    remix_name: string | null;
    artist_id: number | null;
    artist_name: string | null;
    album_id: number | null;
    album_name: string | null;
    duration: number | null;
    cover_art_url: string | null;
}

export interface PlayerLogEntry {
    time: number | null;
    logged_at: string | null;
    track_code: string | null;
    track_name: string | null;
    /** Playable track summary when the entry carries a resolvable track code. */
    track: PlayerLogTrack | null;
    /** Free-text log line reported by the player (command output, sync lines, …). */
    message: string | null;
}

const INSPECTOR_REFETCH_INTERVAL_MS = 30_000;

/**
 * Shared polling posture for the inspector's live cards. vue-query skips
 * interval ticks while the tab is hidden, so an inspector left open in a
 * background tab froze and showed stale state on return. Keep ticking while
 * hidden, and refetch on refocus (per-query override of the app-level
 * refetchOnWindowFocus: false) in case the browser throttled the timer.
 */
const LIVE_POLL_OPTIONS = {
    refetchInterval: INSPECTOR_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
} as const;

/**
 * Query key for one player's pending commands. Shared by the query and the
 * queued-delete mutation so optimistic cache surgery hits the same entry
 * (vue-query unwraps the ref when hashing keys).
 */
function pendingCommandsQueryKey(locationZonePlayerId: ComputedRef<number | null>) {
    return ['location-zone-player-commands', locationZonePlayerId] as const;
}

/** Pending commands (Redis + queue) for one location zone player. */
export function useLocationZonePlayerCommands(locationZonePlayerId: ComputedRef<number | null>) {
    return useQuery({
        queryKey: pendingCommandsQueryKey(locationZonePlayerId),
        queryFn: () =>
            http
                .get(`/api/location_zone_player/${locationZonePlayerId.value}/commands`)
                .then((response) => response.data as PendingCommandsResponse),
        enabled: computed(() => locationZonePlayerId.value !== null),
        ...LIVE_POLL_OPTIONS,
        retry: 0,
    });
}

/**
 * Delete one scheduled (queued) command row before the dispatcher sends it.
 * Only `queued` rows are deletable — `waiting` entries already sit in the
 * legacy Redis list, which has no per-item delete. Optimistic: the row leaves
 * the `queued` bucket immediately and is restored (with an error toast) if the
 * DELETE fails; either way the pending-commands query re-syncs on settle.
 */
export function useDeleteQueuedCommandMutation(locationZonePlayerId: ComputedRef<number | null>) {
    const queryClient = useQueryClient();
    const pendingCommandsKey = pendingCommandsQueryKey(locationZonePlayerId);
    return useMutation({
        mutationFn: (queuedCommandId: number) => http.delete(`/api/command_queue/${queuedCommandId}`),
        onMutate: async (queuedCommandId: number) => {
            // Cancel the 30s poll so an in-flight refetch can't clobber the optimistic removal.
            await queryClient.cancelQueries({ queryKey: pendingCommandsKey });
            const previous = queryClient.getQueryData<PendingCommandsResponse>(pendingCommandsKey);
            if (previous) {
                queryClient.setQueryData<PendingCommandsResponse>(pendingCommandsKey, {
                    ...previous,
                    queued: previous.queued.filter((entry) => entry.id !== queuedCommandId),
                });
            }
            return { previous };
        },
        onError: (error, _queuedCommandId, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(pendingCommandsKey, context.previous);
            }
            const isForbidden = error instanceof AxiosError && error.response?.status === 403;
            toast.error("Couldn't delete the scheduled command", {
                description: isForbidden
                    ? 'You may not have permission to delete scheduled commands.'
                    : undefined,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: pendingCommandsKey });
        },
    });
}

/** The queue's content-delivery command. A queued one is re-timed by a POST, never duplicated. */
const CONTENT_DELIVERY_COMMAND = 'ADDCUSTOM';

/** True when "Send now" on this queued command re-times the waiting delivery rather than re-creating it. */
export function isQueuedContentDelivery(command: string): boolean {
    return command === CONTENT_DELIVERY_COMMAND;
}

/** "YYYY-MM-DD HH:mm:ss" in UTC, the form the queue's scheduled_time column holds. */
export function queueTimeFor(nowMs: number): string {
    return new Date(nowMs).toISOString().slice(0, 19).replace('T', ' ');
}

export interface QueueApi {
    create: (body: { location_zone_player_id: number; command: string; scheduled_time: string }) => Promise<unknown>;
    remove: (queuedCommandId: number) => Promise<unknown>;
}

export interface SendNowResult {
    /** The new row was created but the old one could not be deleted, so the command will go out twice. */
    duplicateLeft: boolean;
}

/**
 * Send a scheduled command now, using only the command_queue resource (it has
 * no update route).
 *
 * A queued ADDCUSTOM is re-timed by one POST: the backend writes ADDCUSTOM
 * through its pending-delivery upsert, which keeps the earlier of the two
 * times. Any other command is created again, due now, and only once that
 * succeeds is the old row deleted. Creating first means a failure can never
 * lose the command. If the delete fails, both rows are left and the caller
 * says so.
 */
export async function sendQueuedCommandNow(
    entry: { id: number; command: string },
    locationZonePlayerId: number,
    nowMs: number,
    queueApi: QueueApi
): Promise<SendNowResult> {
    await queueApi.create({
        location_zone_player_id: locationZonePlayerId,
        command: entry.command,
        scheduled_time: queueTimeFor(nowMs),
    });
    if (isQueuedContentDelivery(entry.command)) {
        return { duplicateLeft: false };
    }
    try {
        await queueApi.remove(entry.id);
    } catch {
        return { duplicateLeft: true };
    }
    return { duplicateLeft: false };
}

const commandQueueApi: QueueApi = {
    create: (body) => http.post('/api/command_queue', body),
    remove: (queuedCommandId) => http.delete(`/api/command_queue/${queuedCommandId}`),
};

/**
 * "Send now" on a scheduled command. The every-minute dispatcher sends due
 * rows, so success means it goes out within a minute. Refreshes the pending
 * commands and the Sync popover's reads afterwards.
 */
export function useSendQueuedCommandNowMutation(locationZonePlayerId: ComputedRef<number | null>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (entry: { id: number; command: string }) =>
            sendQueuedCommandNow(entry, locationZonePlayerId.value as number, Date.now(), commandQueueApi),
        onSuccess: (result) => {
            if (result.duplicateLeft) {
                toast.warning('Sending within a minute', {
                    description: "The scheduled copy couldn't be removed, so it will also go out at its time. Cancel it with ×.",
                });
                return;
            }
            toast.success('Sending within a minute');
        },
        onError: (error) => {
            const isForbidden = error instanceof AxiosError && error.response?.status === 403;
            toast.error("Couldn't send the command now", {
                description: isForbidden ? 'You may not have permission to schedule commands.' : 'It is still scheduled.',
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: pendingCommandsQueryKey(locationZonePlayerId) });
            queryClient.invalidateQueries({ queryKey: [COMMAND_HISTORY_QUERY_ROOT] });
            queryClient.invalidateQueries({ queryKey: ['scheduler-publish-status'] });
            queryClient.invalidateQueries({ queryKey: ['scheduler-pending-changes'] });
        },
    });
}

/**
 * Most-recent-first log history (LzpLogs2 — plays, syncs, command output, …)
 * for one location zone player, server-side filtered by time range, message
 * substring, and track name/code.
 */
export function useLocationZonePlayerLogs(
    locationZonePlayerId: ComputedRef<number | null>,
    filters: () => PlayerLogFilters
) {
    const filterKey = computed(() => filters());
    return useQuery({
        queryKey: ['location-zone-player-logs', locationZonePlayerId, filterKey],
        queryFn: () =>
            http
                .get(`/api/location_zone_player/${locationZonePlayerId.value}/logs`, {
                    params: buildLogQueryParams(filters(), Date.now()),
                })
                .then((response) => response.data as PlayerLogsResponse),
        enabled: computed(() => locationZonePlayerId.value !== null),
        ...LIVE_POLL_OPTIONS,
        // Filter changes re-key the query; keep showing the previous list
        // instead of flashing "Loading…" while the new scan runs.
        placeholderData: keepPreviousData,
        retry: 0,
    });
}

/**
 * One issuable command from the catalogue. Carries the gating bounds so the
 * client can mirror the server's eligibility checks per selected player, plus
 * the appended `display_string` / `is_issuable` the API resource exposes.
 */
export interface CommandEnumOption {
    id: number;
    name: string;
    menu_name: string | null;
    command: string;
    position: number;
    /** Draw a menu separator before this row (groups sections, e.g. before "Clear Stuck Commands"). */
    should_prefix_separator: boolean;
    version_minimum: string | null;
    version_maximum: string | null;
    is_virtual_player_command_only: boolean;
    is_hardware_player_command_only: boolean;
    display_string: string;
    is_issuable: boolean;
    /**
     * True when the command is a server-side special keyword only the legacy
     * (phpapi) handler can execute. Optional — the backend field isn't merged
     * everywhere yet, so absent means "no legacy gating" and the client must
     * behave exactly as before.
     */
    requires_legacy_handler?: boolean;
}

export interface IssueCommandRequest {
    command_enum_id?: number;
    /** Raw command — restricted server-side to trusted user ids. Prefer command_enum_id. */
    command?: string;
    location_zone_player_ids: number[];
    /** Naive local wall-clock 'YYYY-MM-DDTHH:mm:ss'; must be in the future. Omit to issue immediately. */
    scheduled_at?: string | null;
    /** Read scheduled_at in each player's own timezone ("player time"); default true server-side. */
    use_player_timezone?: boolean;
    /** Operator's IANA timezone — the fallback when player time is off or a player has none. */
    timezone?: string;
}

/** Per-player breakdown the issue endpoint returns. */
export interface IssueCommandResult {
    issued: Array<{ location_zone_player_id: number; command: string }>;
    queued: Array<{ location_zone_player_id: number; scheduled_time: string }>;
    skipped: Array<{ location_zone_player_id: number; reason: string }>;
}

const COMMAND_ENUMS_STALE_MS = 5 * 60_000;

/**
 * The issuable-command catalogue (slow-changing reference data). Cached
 * app-wide so the picker opens instantly; the client gates each option per the
 * selected players' version / type before issuing.
 */
export function useLocationZonePlayerCommandEnums() {
    return useQuery({
        queryKey: ['location-zone-player-command-enums'],
        queryFn: () =>
            http.get('/api/location_zone_player_command_enums').then((response) => {
                const payload = response.data as { data?: CommandEnumOption[] } | CommandEnumOption[];
                return Array.isArray(payload) ? payload : (payload.data ?? []);
            }),
        staleTime: COMMAND_ENUMS_STALE_MS,
        retry: 0,
    });
}

/**
 * Issue one command to one or more players (immediate or scheduled). On success
 * refreshes any open inspector's pending-commands list, and marks the command
 * history stale, since the backend records each command as it is issued. The endpoint always 200s
 * with an issued/queued/skipped breakdown — read it to report per-player outcomes.
 */
export function useIssueCommandMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: IssueCommandRequest) =>
            http
                .post('/api/location_zone_player_commands/issue', payload)
                .then((response) => response.data as IssueCommandResult),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['location-zone-player-commands'] });
            queryClient.invalidateQueries({ queryKey: [COMMAND_HISTORY_QUERY_ROOT] });
        },
    });
}
